import { http } from '@/lib/http'

export interface Channel {
  id: string;
  workspaceId: string;
  name: string;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
  unreadCount?: number;
}

export interface ChannelAttachment {
  id: string;
  filename: string;
  mimeType: string;
  url: string;
  size?: number;
}

export interface ChannelMessage {
  id: string;
  channelId: string;
  userId: string;
  userName?: string;
  content: string;
  attachments?: ChannelAttachment[];
  createdAt: string;
  editedAt?: string;
}

export async function listChannels(workspaceId: string, authToken?: string | null): Promise<Channel[]> {
  const res = await http.get<Channel[]>(`/workspaces/${workspaceId}/channels?type=group`, { authToken })
  return res.data
}

export async function createChannel(workspaceId: string, name: string, visibility: 'public' | 'private' = 'public', authToken?: string | null): Promise<Channel> {
  const res = await http.post<Channel>(`/workspaces/${workspaceId}/channels`, { name, visibility }, { authToken })
  return res.data
}

export async function deleteChannel(workspaceId: string, channelId: string, authToken?: string | null): Promise<void> {
  await http.delete(`/workspaces/${workspaceId}/channels/${channelId}`, { authToken })
}

export async function listMessages(workspaceId: string, channelId: string, opts?: { limit?: number; before?: string }, authToken?: string | null): Promise<ChannelMessage[]> {
  const params = new URLSearchParams()
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.before) params.set('before', opts.before)
  const qs = params.toString()
  const res = await http.get<ChannelMessage[]>(`/workspaces/${workspaceId}/channels/${channelId}/messages${qs ? `?${qs}` : ''}`, { authToken })
  return res.data
}

export async function sendMessage(workspaceId: string, channelId: string, content: string, senderName?: string, attachments?: ChannelAttachment[], authToken?: string | null): Promise<ChannelMessage> {
  const res = await http.post<ChannelMessage>(`/workspaces/${workspaceId}/channels/${channelId}/messages`, { content, senderName, ...(attachments?.length ? { attachments } : {}) }, { authToken })
  return res.data
}

export async function markChannelRead(workspaceId: string, channelId: string, authToken?: string | null): Promise<void> {
  await http.post(`/workspaces/${workspaceId}/channels/${channelId}/read`, {}, { authToken })
}

export async function editMessage(workspaceId: string, channelId: string, messageId: string, content: string, authToken?: string | null): Promise<{ id: string; content: string; editedAt: string }> {
  const res = await http.patch<{ id: string; content: string; editedAt: string }>(`/workspaces/${workspaceId}/channels/${channelId}/messages/${messageId}`, { content }, { authToken })
  return res.data
}

export async function deleteMessage(workspaceId: string, channelId: string, messageId: string, authToken?: string | null): Promise<void> {
  await http.delete(`/workspaces/${workspaceId}/channels/${channelId}/messages/${messageId}`, { authToken })
}

export interface ChannelMember {
  userId: string;
  userName: string | null;
  addedAt: string;
}

export async function listChannelMembers(workspaceId: string, channelId: string, authToken?: string | null): Promise<ChannelMember[]> {
  const res = await http.get<ChannelMember[]>(`/workspaces/${workspaceId}/channels/${channelId}/members`, { authToken })
  return res.data
}

export async function addChannelMember(workspaceId: string, channelId: string, userId: string, userName: string, authToken?: string | null): Promise<ChannelMember> {
  const res = await http.post<ChannelMember>(`/workspaces/${workspaceId}/channels/${channelId}/members`, { userId, userName }, { authToken })
  return res.data
}

export async function removeChannelMember(workspaceId: string, channelId: string, userId: string, authToken?: string | null): Promise<void> {
  await http.delete(`/workspaces/${workspaceId}/channels/${channelId}/members/${userId}`, { authToken })
}
